import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import axiosInstance from '../../api/api';
import { BASE_URL } from '../../config/api-routes';
import SecureResumePdfViewer from './SecureResumePdfViewer';

type Props = {
  show: boolean;
  username: string;
  shareToken?: string | null;
  onHide: () => void;
};

export default function ResumePreviewModal({ show, username, shareToken, onHide }: Props) {
  const previewUrl = useMemo(() => {
    const base = `${BASE_URL}u/${encodeURIComponent(username)}/resume-preview`;
    if (!shareToken) return base;
    const params = new URLSearchParams({ recruitertoken: shareToken });
    return `${base}?${params.toString()}`;
  }, [username, shareToken]);

  const [resumeBlob, setResumeBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) {
      setResumeBlob(null);
      setError('');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    void axiosInstance
      .get(previewUrl, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        const blob = response.data as Blob;
        if (!blob || blob.size === 0) {
          setError('Resume preview is not available.');
          return;
        }
        setResumeBlob(blob);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load resume preview. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, previewUrl]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="resume-preview-modal"
      backdropClassName="resume-preview-modal-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>Resume Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="resume-preview-frame-wrap">
          {loading ? (
            <div className="resume-preview-loading">Loading resume preview…</div>
          ) : error ? (
            <div className="resume-preview-error">
              <Alert variant="danger" className="m-3 mb-0">
                {error}
              </Alert>
            </div>
          ) : resumeBlob ? (
            <SecureResumePdfViewer blob={resumeBlob} />
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
